import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { Fragment } from 'react';

import { Link as RouterLink } from '@sorare/routing';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { ANY_SPORT_MY_CLUB_SLUG } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';

import type { MyClubLink_publicUserInfoInterface } from './__generated__/index.graphql.ts';

type Props = PropsWithChildren<
  {
    user: MyClubLink_publicUserInfoInterface;
    sport?: Sport;
    route?: string;
    onClick?: ComponentProps<typeof RouterLink>['onClick'];
  } & {
    WhenSuspended?: FC<React.PropsWithChildren<unknown>>;
  } & (
      | {
          Link?: never;
          className?: string;
        }
      | {
          Link?: FC<
            React.PropsWithChildren<{
              to: ComponentProps<typeof RouterLink>['to'];
              onClick: ComponentProps<typeof RouterLink>['onClick'];
            }>
          >;
          className?: never;
        }
    )
>;

export const MyClubLink = ({
  user,
  sport,
  route = ANY_SPORT_MY_CLUB_SLUG,
  children,
  Link,
  WhenSuspended = Fragment,
  className,
  onClick,
}: Props) => {
  const { sport: currentSport } = useSportContext() || {
    sport: Sport.FOOTBALL,
  };

  if (user.suspended) {
    return <WhenSuspended>{children}</WhenSuspended>;
  }

  const actualSport = sport || currentSport || Sport.FOOTBALL;
  const to = generateSportPath(route, {
    sport: actualSport,
    params: {
      slug: user.slug,
    },
  });

  if (Link) {
    return (
      <Link to={to} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <RouterLink to={to} className={className} onClick={onClick}>
      {children}
    </RouterLink>
  );
};

MyClubLink.fragments = {
  user: gql`
    fragment MyClubLink_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      suspended
    }
  ` as TypedDocumentNode<MyClubLink_publicUserInfoInterface>,
};
